"use client";

import { Vehicle } from "@alba-cars/common-modules";
import { Clock3, MapPin } from "lucide-react";
import { StaticImageData } from "next/image";
import { useLocale, useTranslations } from "next-intl";

import IconDirham from "../Icons/IconDirham";
import CustomButton from "./buttons/Button";
import { CarFavoriteButton } from "./buttons/CarFavoriteButton";
import { CarCardImage } from "./CarCardImage";
import { CarCardSlider } from "./CarCardSlider";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useRouter } from "@/i18n/routing";
import { AgentVehicle } from "@/lib/types/vehicle-agent";
import { formatPriceWithDirham, getCarMonthlyInstallment, getCarPrice, slugify } from "@/lib/utils";
import { isReservedConsignment } from "@/lib/vehicle-ownership";
import { formatTravelDistance, formatTravelDuration } from "@/lib/vehicle-travel";
import defaultVehicleImage from "@/public/assets/images/tesla.jpg";
import { DEFAULT_PLACEHOLDER } from "@/utils/constants";
import { getCarNameTitle } from "@/utils/utils";

interface CarsCardProps {
  car: AgentVehicle & {
    testDriveDate?: Date;
    testDriveRef?: string | number;
    isSignature?: boolean;
    featured?: boolean;
  };
  favoriteButtonCallback?: () => void;
  showBuyThisCar?: boolean;
  showTestDriveDetails?: boolean;
  hideFavoriteButton?: boolean;
  useSlider?: boolean;
  showLocationDetails?: boolean;
  priority?: boolean;
}

function resolveDisplayPrice(car: Vehicle): number {
  const isTimedSaleActive = car.finance?.isTimedSaleActive === true && Number(car.finance?.salePrice) > 0;

  if (isTimedSaleActive) {
    return Number(car.finance!.salePrice);
  }

  const hasDroppedPrice = car.finance?.vehiclePriceType === "dropped_price" && Number(car.finance?.droppedPrice) > 0;

  if (hasDroppedPrice) {
    return Number(car.finance!.droppedPrice);
  }

  return Number(car.finance?.price) || getCarPrice(car);
}

function resolveInstallment(car: Vehicle): number {
  if (car.finance?.isTimedSaleActive === true && Number(car.finance?.saleMonthlyInstallment) > 0) {
    return Number(car.finance.saleMonthlyInstallment);
  }
  if (car.finance?.vehiclePriceType === "dropped_price" && Number(car.finance?.droppedMonthlyInstallment) > 0) {
    return Number(car.finance.droppedMonthlyInstallment);
  }
  return getCarMonthlyInstallment(car);
}

export default function CarsCard({
  car,
  favoriteButtonCallback,
  showBuyThisCar = false,
  showTestDriveDetails = false,
  hideFavoriteButton = false,
  useSlider = true,
  showLocationDetails = false,
  priority = true,
}: CarsCardProps) {
  const router = useRouter();
  const t = useTranslations("CarCard");
  const locale = useLocale();
  const { agentAccess, token } = useAuth();

  if (!car) {
    return null;
  }

  const displayStockNumber = String(car.chassisNumber ?? "")
    .replace(/AC/gi, "")
    .trim();
  const showStockBadge = agentAccess.isSalesAgent && !!token && !!displayStockNumber;

  const mediaUrls: { mediaUrl?: string | StaticImageData; placeholder?: string }[] =
    car?.media
      ?.filter((media) => !!media.mediaUrl && media.mediaType === "image")
      .map((media) => ({
        mediaUrl: media.mediaUrl,
        placeholder: media.placeholder,
      })) || [];

  !mediaUrls.length &&
    mediaUrls.push({
      mediaUrl: defaultVehicleImage,
      placeholder: DEFAULT_PLACEHOLDER,
    });

  const refId = car?.refId || "";
  const trim = car?.trim;
  const makeName = car?.make?.name || "";
  const modelName = car?.model?.name || "";
  const makeModel = getCarNameTitle(car, { showTrim: true });
  const mileage = car?.mileage || 0;
  const year = car?.year || "";
  const displayPrice = resolveDisplayPrice(car);
  const installment = resolveInstallment(car);
  const status = car?.status || "";
  const isPinned = car?.isPinned || false;
  const isBrandNew = car?.isBrandNew || false;
  const isSignature = car?.isSignature || false;
  const isReserved = status === "Reserved";
  const isCarOfTheDay = isReservedConsignment(car);
  const isFeaturedByStockAge = car?.featured || false;

  const getBadges = (): Array<{ type: string; label: string; bgColor: string }> => {
    if (isReserved) {
      return [
        {
          type: isCarOfTheDay ? "carOfTheDay" : "reserved",
          label: isCarOfTheDay ? t("badges.carOfTheDay") : t("badges.reserved"),
          bgColor: "bg-primary text-white",
        },
      ];
    }

    const is212 = makeName.toLowerCase().includes("212");

    let availableBadges: Array<{ type: string; label: string; bgColor: string }> = [];

    if (is212) {
      availableBadges = [
        isPinned && { type: "featured", label: t("badges.featured"), bgColor: "bg-[#ffd100] text-black" },
        isSignature && { type: "signature", label: t("badges.signature"), bgColor: "bg-blue-500 text-white" },
        isBrandNew && { type: "brandNew", label: t("badges.brandNew"), bgColor: "bg-green-500 text-white" },
      ].filter((badge): badge is { type: string; label: string; bgColor: string } => Boolean(badge));
    } else {
      availableBadges = [
        // isPinned && { type: "featured", label: t("badges.featured"), bgColor: "bg-[#ffd100] text-black" },
        isFeaturedByStockAge && { type: "featured", label: t("badges.featured"), bgColor: "bg-[#ffd100] text-black" },
        isSignature && { type: "signature", label: t("badges.signature"), bgColor: "bg-blue-500 text-white" },
        // isBrandNew && { type: "brandNew", label: t("badges.preOwnedBrandNew"), bgColor: "bg-green-500 text-white" },
      ].filter((badge): badge is { type: string; label: string; bgColor: string } => Boolean(badge));
    }

    return availableBadges.slice(0, 2);
  };
  const badges = getBadges();
  const roundTrip = car.locationDistance?.roundTrip;
  const locationLabel = car.locationLabel || car.locationDistance?.vehicleLocation.label;

  const detailsLink = trim
    ? `${refId}-${slugify(makeName)}-${slugify(modelName)}-${slugify(trim)}`
    : `${refId}-${slugify(makeName)}-${slugify(modelName)}`;

  const handleBuyCar = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isCarOfTheDay) {
      return;
    }

    const params = new URLSearchParams();
    params.set("deposit", "5000");
    router.push(`/book_car/${detailsLink}?${params.toString()}`);
  };

  const cardUrl = `/buy-used-cars/vehicle/${detailsLink}`;

  return (
    <div className="h-full">
      <Link href={cardUrl}>
        <div
          className={`hover:shadow-m group relative transform rounded-3xl bg-white p-1 ${isPinned && !isReserved ? "pinned-card-border" : ""}`}
        >
          <div className="relative">
            {badges.length > 0 && (
              <div className="absolute left-3 top-3 z-50 flex flex-wrap items-center gap-2 lg:max-w-56 2xl:max-w-full">
                {badges.map((badge, index) => (
                  <div
                    key={`${badge.type}-${index}`}
                    className={`inter rounded-lg px-3 py-1 text-center text-xs font-semibold ${badge.bgColor}`}
                  >
                    {badge.label}
                  </div>
                ))}
              </div>
            )}

            {showStockBadge ? (
              <div
                className="absolute bottom-3 left-3 z-50 rounded-lg bg-white px-2 py-1 text-center text-xs"
                aria-label={t("stockBadge", { number: displayStockNumber })}
              >
                <span className="inter text-xs font-semibold text-black">
                  {t("stockBadge", { number: displayStockNumber })}
                </span>
              </div>
            ) : null}

            {useSlider ? (
              <CarCardSlider images={mediaUrls} makeModel={makeModel} priority={priority} />
            ) : (
              <CarCardImage
                index={0}
                mediaUrl={car?.media?.[0]?.mediaUrl}
                blurPlaceholder={DEFAULT_PLACEHOLDER}
                makeModel={makeModel}
              />
            )}
          </div>

          {!hideFavoriteButton && <CarFavoriteButton carId={car.id} unfavoriteCallback={favoriteButtonCallback} />}

          <div className="relative p-4">
            <div className="inter line-clamp-2 flex h-[3rem] max-h-[3rem] items-center justify-between text-[0.95rem]">
              <div className="flex w-full items-center justify-between">
                <h3 className="max-w-56 text-wrap font-semibold md:group-hover:text-primary xxxl:max-w-56">
                  {makeModel}
                </h3>
                <span className="font-bold">{year}</span>
              </div>
            </div>

            <div className="inter my-5 border-b-2 border-[#E9E8E8] pb-5">
              <div className="flex items-center justify-between">
                {!!installment && <span className="text-xs font-medium text-[#898989]">{t("startsFrom")}</span>}
                <span className="text-xs font-medium text-[#898989]">{t("fullPrice")}</span>
              </div>

              <div className="mt-1 flex items-center justify-between">
                {!!installment && (
                  <span className="flex items-center gap-0.5 text-sm font-bold text-primary" dir="ltr">
                    <IconDirham width="13" height="13" fill="hsla(var(--primary))" />
                    {formatPriceWithDirham(installment)}
                    {t("perMonth")}
                  </span>
                )}
                <span className="flex items-center gap-0.5 text-sm font-bold text-black" dir="ltr">
                  <IconDirham width="13" height="13" />
                  {formatPriceWithDirham(displayPrice)}
                </span>
              </div>
            </div>

            <div className="inter group flex items-center justify-between">
              <span className="text-xs font-medium text-[#898989]">{t("odometer")}</span>
              <span className="text-sm font-medium text-black">
                {mileage.toLocaleString(locale)} {t("kms")}
              </span>
            </div>
            {showLocationDetails ? (
              <div
                className="inter mt-4 flex min-h-10 items-center justify-between gap-3 rounded-xl bg-secondary-light px-3 py-2 text-xs font-semibold text-primary-foreground"
                aria-label={
                  roundTrip
                    ? t("travelSummary", {
                        location: locationLabel || t("locationUnknown"),
                        duration: formatTravelDuration(roundTrip.durationSeconds, locale),
                        distance: formatTravelDistance(roundTrip.distanceMeters, locale),
                      })
                    : t("locationUnknown")
                }
              >
                <span className="flex min-w-0 items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                  <span className="truncate">
                    {roundTrip ? locationLabel || t("locationUnknown") : t("locationUnknown")}
                  </span>
                </span>
                {roundTrip ? (
                  <span className="ms-auto flex shrink-0 items-center gap-2" dir="ltr">
                    <span className="flex items-center gap-1 text-primary">
                      <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
                      {formatTravelDuration(roundTrip.durationSeconds, locale)}
                    </span>
                    <span className="text-black/50">{formatTravelDistance(roundTrip.distanceMeters, locale)}</span>
                  </span>
                ) : null}
              </div>
            ) : null}
            {showBuyThisCar && (
              <CustomButton
                buttonText={t("buyThisCar")}
                disabled={isCarOfTheDay}
                className="mt-4 w-full rounded-2xl bg-primary py-3 font-inter text-white transition-all duration-300 ease-in-out hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleBuyCar}
              />
            )}
            {showTestDriveDetails && (
              <div className="inter mt-4 w-full rounded-2xl bg-secondary-light py-3 text-center text-sm">
                {t("testDriveBooked")} {car.testDriveDate?.toLocaleDateString(locale)} - {t("ref")}:{car.testDriveRef}
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
